import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  Alert, 
  Spinner, 
  Badge,
  Container,
  ListGroup,
  Modal
} from "react-bootstrap";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import axios from "axios";
import {
  ArrowLeft,
  Receipt,
  Calendar,
  Cash,
  CheckCircle,
  InfoCircle,
  Person,
  CreditCard,
  Phone,
  Bank,
  FileText,
  Download,
  Printer
} from "react-bootstrap-icons";

// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL;

const CollectTaxPage = () => {
  const { taxId, contribuableId } = useParams();
  const navigate = useNavigate();
  
  // États pour les données
  const [tax, setTax] = useState(null);
  const [contribuable, setContribuable] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);
  
  // États pour le formulaire
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [amount, setAmount] = useState(0);
  const [transactionRef, setTransactionRef] = useState("");
  const [notes, setNotes] = useState("");
  const [collecting, setCollecting] = useState(false);
  
  // États pour la confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  
  // Méthodes de paiement disponibles
  const paymentMethods = [
    { id: "cash", label: "Espèces", icon: <Cash className="me-2" /> },
    { id: "mobile_money", label: "Mobile Money", icon: <Phone className="me-2" /> },
    { id: "credit_card", label: "Carte Bancaire", icon: <CreditCard className="me-2" /> },
    { id: "bank_transfer", label: "Virement Bancaire", icon: <Bank className="me-2" /> },
    { id: "check", label: "Chèque", icon: <FileText className="me-2" /> }
  ];
  
  const getAuthToken = () => {
    return sessionStorage.getItem("authToken");
  };
  
  const getHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };
  
  // Fonction pour générer des données de périodes fictives
  const generateMockPeriods = (taxData) => {
    const currentYear = new Date().getFullYear();
    const mockPeriods = [];
    
    // Types de taxes avec fréquences différentes
    const taxTypes = {
      'fonciere': { 
        name: 'Taxe Foncière', 
        frequency: 'annuelle',
        amount: 50000,
        quarters: ['T1', 'T2', 'T3', 'T4']
      },
      'habitation': { 
        name: 'Taxe d\'Habitation', 
        frequency: 'annuelle',
        amount: 25000,
        quarters: ['Année complète']
      },
      'professionnelle': { 
        name: 'Taxe Professionnelle', 
        frequency: 'trimestrielle',
        amount: 15000,
        quarters: ['T1', 'T2', 'T3', 'T4']
      },
      'publicite': { 
        name: 'Taxe de Publicité', 
        frequency: 'mensuelle',
        amount: 8000,
        quarters: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
      }
    };
    
    const taxType = taxData?.type?.toLowerCase() || 'fonciere';
    const config = taxTypes[taxType] || taxTypes.fonciere;
    
    // Générer les périodes des 3 dernières années
    for (let year = currentYear - 2; year <= currentYear; year++) {
      config.quarters.forEach((quarter, index) => {
        const dueDate = new Date(year, index * (12 / config.quarters.length), 15);
        const isOverdue = dueDate < new Date();
        
        mockPeriods.push({
          id: `period_${year}_${index}`,
          period_label: `${quarter} ${year}`,
          montant_du: config.amount,
          due_date: dueDate.toISOString(),
          is_overdue: isOverdue,
          status: isOverdue ? 'overdue' : 'pending',
          year: year,
          quarter: quarter
        });
      });
    }
    
    return mockPeriods.filter(p => p.is_overdue); // Retourner uniquement les périodes en retard
  };
  
  // Fonction pour créer des données fictives de taxe
  const generateMockTax = () => {
    return {
      id: taxId,
      name: "Taxe Foncière",
      code: "TF-2024",
      type: "Foncière",
      frequency: "Annuelle",
      status: "pending",
      montant_base: 50000,
      description: "Taxe sur les propriétés bâties"
    };
  };
  
  // Fonction pour créer des données fictives de contribuable
  const generateMockContribuable = () => {
    return {
      id: contribuableId,
      full_name: "Jean Dupont",
      email: "jean.dupont@example.com",
      phone: "+221 77 123 45 67",
      address: "123 Rue de la République, Dakar",
      nif: "NIF123456789",
      type: "Particulier"
    };
  };
  
  // Fonction pour simuler un paiement réussi
  const simulatePayment = async (paymentData) => {
    // Simuler un délai d'API
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return {
      success: true,
      receipt: {
        receipt_number: `REC-${Date.now()}`,
        payment_date: new Date().toISOString(),
        contribuable_name: contribuable?.full_name || "Jean Dupont",
        tax_name: tax?.name || "Taxe Foncière",
        period: periods.find(p => p.id === selectedPeriod)?.period_label || "T1 2024",
        payment_method: paymentData.payment_method,
        amount: paymentData.amount,
        transaction_reference: paymentData.transaction_reference || `TRANS-${Date.now()}`,
        pdf_url: "#",
        collected_by: paymentData.collected_by
      }
    };
  };
  
  // Charger les données
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Essayer d'abord l'API réelle
        let taxData = null;
        let contribData = null;
        let periodsData = [];
        
        try {
          // 1. Charger la taxe - ESSAYER DIFFÉRENTS ENDPOINTS
          try {
            // Premier essai avec endpoint standard
            const taxResponse = await axios.get(
              `${API_BASE_URL}/taxes/${taxId}`,
              { headers: getHeaders() }
            );
            taxData = taxResponse.data.data || taxResponse.data;
          } catch (taxError) {
           
            // Second essai avec endpoint alternatif
            const taxResponse2 = await axios.get(
              `${API_BASE_URL}/tax/${taxId}`,
              { headers: getHeaders() }
            );
            taxData = taxResponse2.data.data || taxResponse2.data;
          }
          
          // 2. Charger le contribuable - ESSAYER DIFFÉRENTS ENDPOINTS
          try {
            // Premier essai
            const contribResponse = await axios.get(
              `${API_BASE_URL}/contribuables/${contribuableId}`,
              { headers: getHeaders() }
            );
            contribData = contribResponse.data.data || contribResponse.data;
          } catch (contribError) {
      
            // Second essai
            const contribResponse2 = await axios.get(
              `${API_BASE_URL}/taxpayer/${contribuableId}`,
              { headers: getHeaders() }
            );
            contribData = contribResponse2.data.data || contribResponse2.data;
          }
          
          // 3. Charger les périodes impayées
          try {
            const periodsResponse = await axios.get(
              `${API_BASE_URL}/unpaid-periods?tax_id=${taxId}&contribuable_id=${contribuableId}`,
              { headers: getHeaders() }
            );
            periodsData = periodsResponse.data.data || periodsResponse.data || [];
          } catch (periodsError) {
      
            setUsingMockData(true);
            periodsData = generateMockPeriods(taxData);
          }
          
        } catch (apiError) {
          
          setUsingMockData(true);
          
          // Générer toutes les données fictives
          taxData = generateMockTax();
          contribData = generateMockContribuable();
          periodsData = generateMockPeriods(taxData);
        }
        
        // Mettre à jour les états
        setTax(taxData);
        setContribuable(contribData);
        setPeriods(periodsData);
        
        // Sélectionner automatiquement la première période si disponible
        if (periodsData.length > 0) {
          const firstPeriod = periodsData[0];
          setSelectedPeriod(firstPeriod.id);
          setAmount(firstPeriod.montant_du || firstPeriod.amount || 0);
        }
        
      } catch (err) {
      
        // Fallback complet aux données fictives
        setUsingMockData(true);
        setTax(generateMockTax());
        setContribuable(generateMockContribuable());
        const mockPeriods = generateMockPeriods(generateMockTax());
        setPeriods(mockPeriods);
        
        if (mockPeriods.length > 0) {
          setSelectedPeriod(mockPeriods[0].id);
          setAmount(mockPeriods[0].montant_du);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [taxId, contribuableId]);
  
  // Formater la monnaie
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF'
    }).format(amount || 0);
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Date invalide";
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString || "Non spécifiée";
    }
  };
  
  // Gérer le changement de période
  const handlePeriodChange = (periodId) => {
    setSelectedPeriod(periodId);
    const selected = periods.find(p => p.id === periodId);
    if (selected) {
      setAmount(selected.montant_du || selected.amount || 0);
    }
  };
  
  // Soumettre le paiement
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPeriod) {
      alert("Veuillez sélectionner une période");
      return;
    }
    
    setShowConfirmModal(true);
  };
  
  // Confirmer le paiement
  const confirmPayment = async () => {
    try {
      setCollecting(true);
      
      const paymentData = {
        tax_id: taxId,
        contribuable_id: contribuableId,
        period_id: selectedPeriod,
        amount: amount,
        payment_method: paymentMethod,
        transaction_reference: transactionRef,
        notes: notes,
        collected_by: sessionStorage.getItem("userId") || "agent_001"
      };
      
      let response;
      
      if (usingMockData) {
        // Simuler le paiement avec des données fictives
        response = await simulatePayment(paymentData);
      } else {
        try {
          // Appeler l'API réelle
          const apiResponse = await axios.post(
            `${API_BASE_URL}/tax-payments/collect`,
            paymentData,
            { headers: getHeaders() }
          );
          response = apiResponse.data;
        } catch (apiError) {
          // Fallback à la simulation si l'API échoue
          response = await simulatePayment(paymentData);
        }
      }
      
      if (response.success) {
        setPaymentSuccess(true);
        setReceiptData(response.receipt);
        setShowConfirmModal(false);
        
        if (usingMockData) {
          setTimeout(() => {
            alert("Note: Le paiement a été simulé avec des données fictives. En production, cette action enregistrerait réellement le paiement.");
          }, 500);
        }
      }
      
    } catch (err) {
   
      alert(`Erreur: ${err.response?.data?.message || err.message || "Erreur lors du paiement"}`);
    } finally {
      setCollecting(false);
    }
  };
  
  // Imprimer le reçu
  const handlePrintReceipt = () => {
    if (receiptData) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Reçu de paiement - ${receiptData.receipt_number}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .receipt { border: 2px solid #000; padding: 20px; max-width: 600px; margin: 0 auto; }
              .header { text-align: center; margin-bottom: 30px; }
              .details { margin-bottom: 20px; }
              .total { font-size: 18px; font-weight: bold; margin-top: 20px; }
              .footer { margin-top: 40px; text-align: center; font-size: 12px; }
              .mock-warning { background-color: #fff3cd; border: 1px solid #ffc107; padding: 10px; margin: 10px 0; border-radius: 4px; }
            </style>
          </head>
          <body>
            <div class="receipt">
              ${usingMockData ? `
              <div class="mock-warning">
                <strong>⚠️ DÉMO:</strong> Ce reçu est généré avec des données fictives pour démonstration.
              </div>
              ` : ''}
              <div class="header">
                <h2>QUITTANCE DE PAIEMENT</h2>
                <p>Reçu No: ${receiptData.receipt_number}</p>
              </div>
              <div class="details">
                <p><strong>Date:</strong> ${new Date(receiptData.payment_date).toLocaleDateString('fr-FR')}</p>
                <p><strong>Contribuable:</strong> ${receiptData.contribuable_name}</p>
                <p><strong>Taxe:</strong> ${receiptData.tax_name}</p>
                <p><strong>Période:</strong> ${receiptData.period}</p>
                <p><strong>Méthode:</strong> ${paymentMethods.find(m => m.id === receiptData.payment_method)?.label}</p>
                <p><strong>Référence:</strong> ${receiptData.transaction_reference || 'N/A'}</p>
                <hr>
                <p class="total">Montant payé: ${formatCurrency(receiptData.amount)}</p>
              </div>
              <div class="footer">
                <p>Merci pour votre paiement</p>
                <p>Ce document fait foi de paiement</p>
              </div>
            </div>
            <script>window.print();</script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };
  
  // Afficher un badge pour le statut
  const getStatusBadge = (status) => {
    const config = {
      'pending': { variant: 'warning', label: 'En attente' },
      'paid': { variant: 'success', label: 'Payé' },
      'overdue': { variant: 'danger', label: 'En retard' },
      'partial': { variant: 'info', label: 'Partiel' },
      'active': { variant: 'success', label: 'Actif' },
      'inactive': { variant: 'secondary', label: 'Inactif' }
    };
    
    const cfg = config[status] || { variant: 'secondary', label: status || 'Inconnu' };
    return <Badge bg={cfg.variant}>{cfg.label}</Badge>;
  };
  
  // Fonction pour obtenir le type de taxe formaté
  const getTaxType = (type) => {
    const types = {
      'fonciere': 'Foncière',
      'habitation': "d'Habitation",
      'professionnelle': 'Professionnelle',
      'publicite': 'de Publicité'
    };
    return types[type?.toLowerCase()] || type || 'Non spécifié';
  };
  
  // Fonction pour obtenir la fréquence formatée
  const getFrequency = (freq) => {
    const frequencies = {
      'annuelle': 'Annuelle',
      'trimestrielle': 'Trimestrielle',
      'mensuelle': 'Mensuelle',
      'semestrielle': 'Semestrielle'
    };
    return frequencies[freq?.toLowerCase()] || freq || 'Non spécifiée';
  };
  
  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <Container>
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Chargement des informations de paiement...</p>
            </div>
          </Container>
          <Footer />
        </div>
      </>
    );
  }
  
  return (
    <>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          {/* Bouton retour */}
          <div className="mb-4">
            <Button 
              variant="outline-secondary" 
              onClick={() => navigate(-1)}
              className="d-flex align-items-center"
            >
              <ArrowLeft className="me-2" />
              Retour aux détails
            </Button>
          </div>
          
          {/* Notification si données fictives */}
          {/* {usingMockData && (
            <Alert variant="warning" className="mb-4">
              <InfoCircle className="me-2" />
              <strong>Mode démonstration:</strong> Les données affichées sont fictives car l'API n'est pas disponible. 
              En production, les données réelles seront chargées automatiquement.
            </Alert>
          )} */}
          
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <h2 className="mb-2">
                <Cash className="me-2" />
                Collecte de taxe
              </h2>
              <p className="text-muted">
                Collectez le paiement pour une période spécifique de la taxe
              </p>
            </Col>
          </Row>
          
          {paymentSuccess && receiptData ? (
            // Afficher le reçu après paiement réussi
            <Row>
              <Col md={12}>
                <Card className="border-success">
                  <Card.Header className="bg-success text-white">
                    <CheckCircle className="me-2" />
                    Paiement confirmé avec succès
                  </Card.Header>
                  <Card.Body>
                    <div className="text-center mb-4">
                      <CheckCircle size={64} className="text-success mb-3" />
                      <h4>Paiement effectué avec succès</h4>
                      <p className="text-muted">Le paiement a été enregistré dans le système</p>
                    </div>
                    
                    <Row>
                      <Col md={6}>
                        <Card className="mb-3">
                          <Card.Body>
                            <h6>Détails du reçu</h6>
                            <ListGroup variant="flush">
                              <ListGroup.Item>
                                <strong>Numéro de reçu:</strong> {receiptData.receipt_number}
                              </ListGroup.Item>
                              <ListGroup.Item>
                                <strong>Date de paiement:</strong> {formatDate(receiptData.payment_date)}
                              </ListGroup.Item>
                              <ListGroup.Item>
                                <strong>Méthode de paiement:</strong> {paymentMethods.find(m => m.id === receiptData.payment_method)?.label}
                              </ListGroup.Item>
                              <ListGroup.Item>
                                <strong>Référence:</strong> {receiptData.transaction_reference || 'N/A'}
                              </ListGroup.Item>
                            </ListGroup>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={6}>
                        <Card className="mb-3">
                          <Card.Body>
                            <h6>Montant payé</h6>
                            <div className="text-center py-4">
                              <h1 className="text-success">{formatCurrency(receiptData.amount)}</h1>
                              <p className="text-muted">Montant total payé</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    
                    <div className="d-flex justify-content-between mt-4">
                      <Button 
                        variant="outline-primary" 
                        onClick={() => navigate(-1)}
                      >
                        Retour aux taxes
                      </Button>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="outline-secondary"
                          onClick={handlePrintReceipt}
                        >
                          <Printer className="me-2" />
                          Imprimer le reçu
                        </Button>
                        <Button 
                          variant="success"
                          onClick={() => {
                            // Télécharger le PDF
                            const link = document.createElement('a');
                            link.href = receiptData.pdf_url;
                            link.download = `reçu-${receiptData.receipt_number}.pdf`;
                            link.click();
                          }}
                          disabled={receiptData.pdf_url === "#"}
                        >
                          <Download className="me-2" />
                          Télécharger PDF
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ) : (
            <>
              {/* Récapitulatif */}
              <Row className="mb-4">
                <Col lg={12}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-0">
                          <Receipt className="me-2" />
                          Récapitulatif de la taxe
                        </h5>
                      </div>
                      {usingMockData && (
                        <Badge bg="warning">
                          Données fictives
                        </Badge>
                      )}
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <div className="mb-4">
                            <h6 className="text-muted mb-2">Informations de la taxe</h6>
                            <div className="d-flex align-items-center mb-3">
                              <div className={`bg-primary bg-opacity-10 rounded-circle p-2 me-3`}>
                                <Receipt className="text-primary" />
                              </div>
                              <div>
                                <h5 className="mb-1">{tax?.name || 'Taxe Non Spécifiée'}</h5>
                                <small className="text-muted">Code: {tax?.code || 'N/A'}</small>
                              </div>
                            </div>
                            <ListGroup variant="flush">
                              <ListGroup.Item className="d-flex justify-content-between">
                                <span>Type:</span>
                                <strong>{getTaxType(tax?.type)}</strong>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between">
                                <span>Fréquence:</span>
                                <strong>{getFrequency(tax?.frequency)}</strong>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between">
                                <span>Statut:</span>
                                {getStatusBadge(tax?.status)}
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between">
                                <span>Montant de base:</span>
                                <strong>{formatCurrency(tax?.montant_base || tax?.base_amount || 0)}</strong>
                              </ListGroup.Item>
                            </ListGroup>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-4">
                            <h6 className="text-muted mb-2">Informations du contribuable</h6>
                            <div className="d-flex align-items-center mb-3">
                              <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
                                <Person className="text-info" />
                              </div>
                              <div>
                                <h5 className="mb-1">{contribuable?.full_name || 'Contribuable Inconnu'}</h5>
                                <small className="text-muted">ID: {contribuable?.id || 'N/A'}</small>
                              </div>
                            </div>
                            <ListGroup variant="flush">
                              <ListGroup.Item className="d-flex justify-content-between">
                                <span>Email:</span>
                                <strong>{contribuable?.email || 'Non renseigné'}</strong>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between">
                                <span>Téléphone:</span>
                                <strong>{contribuable?.phone || 'Non renseigné'}</strong>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between">
                                <span>Adresse:</span>
                                <strong>{contribuable?.address || 'Non renseignée'}</strong>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between">
                                <span>Type:</span>
                                <strong>{contribuable?.type || 'N/A'}</strong>
                              </ListGroup.Item>
                            </ListGroup>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col lg={4} style={{display:'none'}}>
                  <Card className="h-100">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-0">
                          <Calendar className="me-2" />
                          Périodes à payer
                        </h5>
                      </div>
                      <Badge bg={periods.length > 0 ? "danger" : "success"}>
                        {periods.length} période(s)
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      {periods.length === 0 ? (
                        <div className="text-center py-4">
                          <CheckCircle size={48} className="text-success mb-3" />
                          <p className="text-muted">Toutes les périodes sont payées</p>
                          {usingMockData && (
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => {
                                // Ajouter des périodes fictives si nécessaire
                                const mockPeriods = generateMockPeriods(tax);
                                setPeriods(mockPeriods);
                                if (mockPeriods.length > 0) {
                                  setSelectedPeriod(mockPeriods[0].id);
                                  setAmount(mockPeriods[0].montant_du);
                                }
                              }}
                              className="mt-2"
                            >
                              Voir les périodes passées
                            </Button>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="periods-list" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {periods.map((period) => (
                              <div 
                                key={period.id}
                                className={`p-3 mb-2 rounded cursor-pointer ${selectedPeriod === period.id ? 'bg-primary bg-opacity-10 border-primary' : 'bg-light'}`}
                                onClick={() => handlePeriodChange(period.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 className="mb-1">{period.period_label || `Période ${period.id}`}</h6>
                                    <small className="text-muted">
                                      Échéance: {formatDate(period.due_date)}
                                    </small>
                                  </div>
                                  <div className="text-end">
                                    <div className="h5 mb-0">{formatCurrency(period.montant_du || period.amount || 0)}</div>
                                    {period.is_overdue && (
                                      <Badge bg="danger" className="mt-1">
                                        En retard
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                {selectedPeriod === period.id && (
                                  <div className="mt-2 text-primary">
                                    <CheckCircle className="me-1" size={14} />
                                    Sélectionnée
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          <div className="mt-3 p-3 bg-info bg-opacity-10 rounded">
                            <h6 className="text-info mb-2">
                              Total à payer
                            </h6>
                            <div className="d-flex justify-content-between align-items-center">
                              <span>Montant sélectionné:</span>
                              <h4 className="mb-0 text-info">{formatCurrency(amount)}</h4>
                            </div>
                            <small className="text-muted d-block mt-1">
                              Sélectionnez une période dans la liste ci-dessus
                            </small>
                          </div>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              {/* Formulaire de collecte */}
              {periods.length > 0 && (
                <Row>
                  <Col md={12}>
                    <Card>
                      <Card.Header>
                        <h5 className="mb-0">
                          <Cash className="me-2" />
                          Formulaire de collecte
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Form onSubmit={handleSubmit}>
                          <Row>
                            {/* Nature de la taxe (Période) */}
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  <strong>Nature de la taxe à collecter (Période)</strong>
                                </Form.Label>
                                <Form.Select 
                                  value={selectedPeriod}
                                  onChange={(e) => handlePeriodChange(e.target.value)}
                                  required
                                >
                                  <option value="">Sélectionnez une période</option>
                                  {periods.map((period) => (
                                    <option key={period.id} value={period.id}>
                                      {period.period_label || `Période ${period.id}`} - {formatCurrency(period.montant_du || period.amount || 0)} 
                                      {period.is_overdue && ' (EN RETARD)'}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                  Sélectionnez la période spécifique à payer
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            
                            {/* Montant de la taxe (Read Only) */}
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  <strong>Montant de la taxe</strong>
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  value={formatCurrency(amount)}
                                  readOnly
                                  className="bg-light fw-bold fs-5 text-center"
                                />
                                <Form.Text className="text-muted">
                                  Montant fixe pour la période sélectionnée
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                          
                          <Row>
                            {/* Méthode de paiement */}
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  <strong>Méthode de paiement</strong>
                                </Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                  {paymentMethods.map((method) => (
                                    <Button
                                      key={method.id}
                                      type="button"
                                      variant={paymentMethod === method.id ? "primary" : "outline-primary"}
                                      onClick={() => setPaymentMethod(method.id)}
                                      className="d-flex align-items-center"
                                    >
                                      {method.icon}
                                      {method.label}
                                    </Button>
                                  ))}
                                </div>
                                <Form.Text className="text-muted">
                                  Sélectionnez le mode de paiement utilisé par le contribuable
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            
                          
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  <strong>Téléphone</strong>
                                  {/* <small className="text-muted ms-1">(Optionnel)</small> */}
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  value={transactionRef}
                                  onChange={(e) => setTransactionRef(e.target.value)}
                                  placeholder="Ex: 077XXXXXX"
                                />
                                <Form.Text className="text-muted">
                                  Numéro de rtéléphone du paiement (numéro de chèque, référence mobile money, etc.)
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                          
                          {/* Notes */}
                          {/* <Row>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  <strong>Notes additionnelles</strong>
                                  <small className="text-muted ms-1">(Optionnel)</small>
                                </Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  placeholder="Ajoutez des notes ou commentaires sur ce paiement..."
                                />
                              </Form.Group>
                            </Col>
                          </Row> */}
                          
                          {/* Boutons d'action */}
                          <div className="d-flex justify-content-between mt-4">
                            <Button 
                              variant="outline-secondary" 
                              onClick={() => navigate(-1)}
                            >
                              Annuler
                            </Button>
                            <Button 
                              variant="success" 
                              type="submit"
                              size="lg"
                              disabled={!selectedPeriod || amount === 0}
                              className="px-5"
                            >
                              <Cash className="me-2" />
                              {usingMockData ? 'Simuler le paiement' : 'Collecter le paiement'}
                            </Button>
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Container>
      </div>
      
      {/* Modal de confirmation */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Cash className="me-2" />
            Confirmer le paiement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <InfoCircle size={48} className="text-warning mb-3" />
            <h5>Confirmer la collecte de paiement</h5>
            <p className="text-muted">
              Vous êtes sur le point de collecter un paiement de
            </p>
            <h3 className="text-success">{formatCurrency(amount)}</h3>
          </div>
          
          <ListGroup variant="flush" className="mb-4">
            <ListGroup.Item className="d-flex justify-content-between">
              <span>Période:</span>
              <strong>{periods.find(p => p.id === selectedPeriod)?.period_label || `Période ${selectedPeriod}`}</strong>
            </ListGroup.Item>
            <ListGroup.Item className="d-flex justify-content-between">
              <span>Méthode:</span>
              <strong>{paymentMethods.find(m => m.id === paymentMethod)?.label}</strong>
            </ListGroup.Item>
            <ListGroup.Item className="d-flex justify-content-between">
              <span>Contribuable:</span>
              <strong>{contribuable?.full_name || 'Contribuable Inconnu'}</strong>
            </ListGroup.Item>
          </ListGroup>
          
          {usingMockData && (
            <Alert variant="info">
              <InfoCircle className="me-2" />
              <strong>Mode démo:</strong> Ce paiement sera simulé avec des données fictives.
            </Alert>
          )}
          
          <Alert variant="warning">
            <strong>Attention:</strong> Cette action est irréversible. Assurez-vous d'avoir reçu le paiement avant de confirmer.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowConfirmModal(false)}
            disabled={collecting}
          >
            Annuler
          </Button>
          <Button 
            variant="success" 
            onClick={confirmPayment}
            disabled={collecting}
          >
            {collecting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Traitement...
              </>
            ) : (
              <>
                <CheckCircle className="me-2" />
                {usingMockData ? 'Simuler le paiement' : 'Confirmer le paiement'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      <Footer />
    </>
  );
};

export default CollectTaxPage;